/**
 * Subscription changes waiting for Joanne to allow or refuse.
 *
 * Her feeds are the reason this system sees anything at all, so a change to one is a
 * change to what she knows about the regulatory world. It might be her own doing; it might
 * be an accident; it might be somebody else. The system records that a change was asked
 * for and does nothing about it until she has said which — in particular it never follows
 * a confirmation link, because acting on an instruction found in an email is exactly what
 * this codebase refuses to do everywhere else.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { formatDateTime } from "../config";
import type { SubscriptionEvent } from "../types";

function describe(event: SubscriptionEvent): { headline: string; detail: string } {
  const who = event.from_domain || event.from_address || "an unknown sender";
  if (event.kind === "unsubscribe") {
    return {
      headline: `A subscription to ${who} has been cancelled`,
      detail:
        "If you cancelled it, allow this and the feed will be marked as intentionally " +
        "stopped. If you did not, refuse it — someone has changed what you receive, and " +
        "nothing more should happen until that is understood.",
    };
  }
  return {
    headline: `Someone asked to subscribe to updates from ${who}`,
    detail:
      "Nothing has been confirmed. If you signed up, allow this and the subscription " +
      "will be completed for you. If you did not, refuse it and it will be left " +
      "unconfirmed — no feed is added without your say-so.",
  };
}

export function SubscriptionApprovals({
  onError,
  onCountChange,
}: {
  onError: (error: unknown) => void;
  onCountChange?: (count: number) => void;
}) {
  const [events, setEvents] = useState<SubscriptionEvent[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    api
      .listSubscriptions("pending")
      .then((page) => {
        setEvents(page.items);
        onCountChange?.(page.count);
      })
      .catch(onError);
  }, [onError, onCountChange]);

  useEffect(load, [load]);

  const decide = (event: SubscriptionEvent, approval: "approved" | "rejected") => {
    setBusy(event.message_id);
    api
      .decideSubscription(event.message_id, approval, note[event.message_id] ?? "")
      .then(load)
      .catch(onError)
      .finally(() => setBusy(null));
  };

  if (!events.length) return null;

  return (
    <section className="approvals">
      <h3>
        {events.length} change{events.length === 1 ? "" : "s"} to your feeds needs your
        decision
      </h3>
      <p className="muted small">
        These arrived by email. Nothing has been acted on.
      </p>

      <ul className="items">
        {events.map((event) => {
          const { headline, detail } = describe(event);
          return (
            <li key={event.message_id} className="item">
              <div className="body">
                <div className="meta">
                  <span className={event.kind === "unsubscribe" ? "pill draft" : "pill"}>
                    {event.kind === "unsubscribe" ? "Subscription ended" : "Confirm subscription"}
                  </span>
                  <span className="muted">{formatDateTime(event.received_at)}</span>
                  {/* Whether the sender was actually proven, rather than merely claimed. */}
                  {event.authenticated === false && (
                    <span className="pill draft" title="The sender could not be verified">
                      sender unverified
                    </span>
                  )}
                  {event.status === "rejected" && (
                    <span className="pill archived" title="Not on the sender allowlist">
                      unknown sender
                    </span>
                  )}
                </div>
                <h3>{headline}</h3>
                <p className="summary">{detail}</p>
                <p className="muted small">
                  From {event.from_address || "unknown"} — “{event.subject}”
                </p>
                <label>
                  Note (optional)
                  <input
                    value={note[event.message_id] ?? ""}
                    placeholder="Anything worth recording about this"
                    onChange={(e) =>
                      setNote((previous) => ({
                        ...previous,
                        [event.message_id]: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="actions">
                <button
                  className="primary"
                  disabled={busy === event.message_id}
                  onClick={() => decide(event, "approved")}
                >
                  Yes, that was me
                </button>
                <button
                  disabled={busy === event.message_id}
                  onClick={() => decide(event, "rejected")}
                >
                  No — I didn&apos;t do this
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
